import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, Space, Button, InputNumber, Modal, DatePicker } from 'antd';
import { DownloadOutlined, UploadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { productsActions } from '../../../store/slices/settings-slices';
import { productsService } from '../../../services/settings-services';
import { ordersService } from '../../../services/orders-services';
import css from './StorageProductsComponent.module.css';

const { RangePicker } = DatePicker;


const WeightCell = ({ record, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(record.weight ?? 0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editing) setValue(record.weight ?? 0);
    }, [record.weight, editing]);

    const save = async () => {
        if (value === (record.weight ?? 0)) {
            setEditing(false);
            return;
        }
        try {
            setSaving(true);
            await productsService.updateProductById(record.id, { weight: value });
            toast.success('Weight updated');
            onSaved();
        } catch {
            toast.error('Failed to update weight');
        } finally {
            setSaving(false);
            setEditing(false);
        }
    };

    if (editing) {
        return (
            <InputNumber
                autoFocus
                size="small"
                min={0}
                step={1}
                value={value}
                onChange={(v) => setValue(v)}
                onBlur={save}
                onPressEnter={save}
                disabled={saving}
                style={{ width: 90 }}
            />
        );
    }

    return (
        <span
            className={css.weightCell}
            onClick={() => setEditing(true)}
            title="Click to edit"
        >
            {record.weight ?? 0}
        </span>
    );
};


const StorageProductsComponent = () => {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const fileInputRef = useRef(null);

    let { productsArray, productsFiltered, isLoading, trigger } = useSelector(store => store.products);

    const [searchQuery, setSearchQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const [notFoundNames, setNotFoundNames] = useState([]);
    const [deductModalOpen, setDeductModalOpen] = useState(false);
    const [deductDateRange, setDeductDateRange] = useState(null);
    const [deducting, setDeducting] = useState(false);
    const [deductResult, setDeductResult] = useState(null);
    const [pinnedIds, setPinnedIds] = useState(new Set());

    useEffect(() => {
        dispatch(productsActions.getAllProducts());
    }, [dispatch, trigger]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        dispatch(productsActions.filteredProducts(value));
    };

    const handleWeightSaved = () => {
        dispatch(productsActions.changeTrigger());
    };

    const sortedProducts = useMemo(() => {
        const list = searchQuery ? (productsFiltered || []) : (productsArray || []);
        return [...list].sort((a, b) => {
            const aPinned = pinnedIds.has(a.id) ? 1 : 0;
            const bPinned = pinnedIds.has(b.id) ? 1 : 0;
            if (bPinned !== aPinned) return bPinned - aPinned;
            return parseFloat(b.weight ?? 0) - parseFloat(a.weight ?? 0);
        });
    }, [productsArray, productsFiltered, searchQuery, pinnedIds]);

    const handleExportXls = async () => {
        if (!sortedProducts.length) {
            toast.error('No products to export');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Storage');

        worksheet.columns = [
            { width: 40 },
            { width: 15 },
        ];

        const headerRow = worksheet.addRow(['Product', 'Weight']);
        headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } };
            cell.alignment = { horizontal: 'center' };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        sortedProducts.forEach((product) => {
            const name = product.name?.[currentLanguage] || product.name?.['en'] || '';
            const row = worksheet.addRow([name, product.weight ?? 0]);
            row.eachCell((cell) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5CC' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'storage_products.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handleUploadXls = async (e) => {
        const file = e.target.files?.[0];
        if (!fileInputRef.current) return;
        fileInputRef.current.value = '';

        if (!file) return;

        setNotFoundNames([]);
        setUploading(true);
        try {
            const buffer = await file.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(buffer);

            const worksheet = workbook.worksheets[0];
            if (!worksheet) {
                toast.error('No worksheet found in file');
                return;
            }

            // Build a name → product map from all language variants
            const nameMap = {};
            (productsArray || []).forEach((product) => {
                const names = product.name || {};
                Object.values(names).forEach((n) => {
                    if (n) nameMap[n.toLowerCase().trim()] = product;
                });
            });

            // Parse rows — skip header row (row 1)
            const updates = [];
            const notFound = [];
            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return;
                const nameCell = row.getCell(1).value;
                const weightCell = row.getCell(2).value;
                if (!nameCell) return;

                const nameStr = String(nameCell).toLowerCase().trim();
                const weight = parseFloat(weightCell);
                if (isNaN(weight)) return;

                const product = nameMap[nameStr];
                if (product) {
                    updates.push({ id: product.id, weight });
                } else {
                    notFound.push(String(nameCell).trim());
                }
            });

            if (!updates.length && !notFound.length) {
                toast.warning('No valid rows found in the file');
                return;
            }

            if (updates.length > 0) {
                const result = await productsService.bulkUpdateWeight(updates);
                setPinnedIds(new Set(updates.map(u => u.id)));
                dispatch(productsActions.changeTrigger());
                toast.success(`Updated ${result.updated} product${result.updated !== 1 ? 's' : ''}`);
            }

            setNotFoundNames(notFound);
        } catch (err) {
            console.error(err);
            toast.error('Failed to parse file');
        } finally {
            setUploading(false);
        }
    };

    const handleDeductConfirm = async () => {
        if (!deductDateRange) return;
        const [start, end] = deductDateRange;
        setDeducting(true);
        try {
            const result = await ordersService.deductStorage(
                start.format('YYYY-MM-DD'),
                end.format('YYYY-MM-DD')
            );
            setPinnedIds(new Set());
            dispatch(productsActions.changeTrigger());
            setDeductResult(result.items || []);
        } catch {
            toast.error('Failed to deduct storage');
        } finally {
            setDeducting(false);
        }
    };

    const handleDeductClose = () => {
        setDeductModalOpen(false);
        setDeductDateRange(null);
        setDeductResult(null);
    };

    const columns = [
        {
            title: 'Product',
            dataIndex: 'name',
            key: 'name',
            render: (name) => name?.[currentLanguage] || name?.['en'] || '—',
        },
        {
            title: 'Weight',
            key: 'weight',
            width: 150,
            align: 'center',
            render: (_, record) => (
                <WeightCell record={record} onSaved={handleWeightSaved} />
            ),
        },
    ];

    return (
        <>
        <div className={css.wrap}>
            <Space
                direction="horizontal"
                size="middle"
                className={css.toolbar}
            >
                <input
                    className={css.searchInput}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search products..."
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleUploadXls}
                />
                <Button
                    icon={<UploadOutlined />}
                    loading={uploading}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Upload XLS
                </Button>
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportXls}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                    Export XLS
                </Button>
                <Button
                    type="primary"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => setDeductModalOpen(true)}
                >
                    Use Products
                </Button>
            </Space>

            {notFoundNames.length > 0 && (
                <div className={css.notFound}>
                    <strong>Not found in database ({notFoundNames.length}):</strong>{' '}
                    {notFoundNames.join(', ')}
                </div>
            )}

            <Table
                dataSource={sortedProducts}
                columns={columns}
                rowKey="id"
                pagination={false}
                loading={isLoading || uploading}
                bordered
                size="small"
                locale={{ emptyText: 'No products' }}
                scroll={{ y: 'calc(100vh - 280px)' }}
            />
        </div>

        <Modal
            title="Use Products"
            open={deductModalOpen}
            onCancel={handleDeductClose}
            onOk={deductResult ? handleDeductClose : handleDeductConfirm}
            okText={deductResult ? 'Close' : 'Deduct'}
            okButtonProps={{ danger: !deductResult, loading: deducting, disabled: !deductResult && !deductDateRange }}
            cancelButtonProps={{ style: { display: deductResult ? 'none' : undefined }, disabled: deducting }}
            width={deductResult ? 560 : 420}
        >
            {deductResult ? (
                <div>
                    {deductResult.length === 0 ? (
                        <p style={{ color: '#8c8c8c', textAlign: 'center', padding: '16px 0' }}>
                            No products had stock to deduct for this date range.
                        </p>
                    ) : (
                        <>
                            <p style={{ marginBottom: 12, color: '#595959' }}>
                                <strong>{deductResult.length}</strong> ingredient{deductResult.length !== 1 ? 's' : ''} deducted:
                            </p>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                        <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>Ingredient</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600 }}>Used</th>
                                        <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600 }}>Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deductResult.map((item, i) => (
                                        <tr key={item.productId} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                            <td style={{ padding: '7px 10px' }}>{item.name?.[currentLanguage] || item.name?.en || item.name}</td>
                                            <td style={{ padding: '7px 10px', textAlign: 'right', color: '#cf1322', fontVariantNumeric: 'tabular-nums' }}>−{item.deducted}</td>
                                            <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: item.remaining === 0 ? '#cf1322' : '#389e0d' }}>{item.remaining}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <p style={{ marginBottom: 16, color: '#595959' }}>
                        Select a date or range. Product weights in storage will be reduced by the total consumed in orders for those days.
                    </p>
                    <RangePicker
                        value={deductDateRange}
                        onChange={(dates) => setDeductDateRange(dates)}
                        format="YYYY-MM-DD"
                        style={{ width: '100%' }}
                        allowSingleDate
                        defaultValue={[dayjs(), dayjs()]}
                    />
                </>
            )}
        </Modal>
        </>
    );
};

export default StorageProductsComponent;
